import { useMutation, useQuery } from "@tanstack/react-query";

import { e2eAgentApi } from "../api/e2eAgentClient";
import type { components } from "../api/e2eAgentSchema";

export type CliRelease = components["schemas"]["CliReleaseOut"];
export type CliReleaseTarget = components["schemas"]["CliReleaseTarget"];
export type CliReleaseDownload = components["schemas"]["CliReleaseDownloadOut"];

export const cliReleaseQueryKey = [
  "e2e-agent",
  "cli-release",
  "latest",
] as const;

function ensure<Data>(data: Data | undefined, error: unknown): Data {
  if (error !== undefined) {
    const detail =
      error && typeof error === "object" && "detail" in error
        ? JSON.stringify(error.detail)
        : JSON.stringify(error);
    throw new Error(`CLI release request failed: ${detail}`);
  }
  if (data === undefined) throw new Error("CLI release response was empty.");
  return data;
}

export function useLatestCliRelease() {
  return useQuery({
    queryKey: cliReleaseQueryKey,
    queryFn: async () => {
      const { data, error } = await e2eAgentApi.GET("/v1/cli/releases/latest");
      return ensure(data, error);
    },
    staleTime: 5 * 60_000,
  });
}

export function useCliReleaseDownload() {
  return useMutation({
    mutationFn: async ({
      version,
      target,
    }: {
      version: string;
      target: CliReleaseTarget;
    }) => {
      const { data, error } = await e2eAgentApi.POST(
        "/v1/cli/releases/{version}/download-link",
        {
          params: { path: { version } },
          body: { target },
        },
      );
      return ensure(data, error);
    },
  });
}
